import { useCallback } from "react";

import { FormInstance } from "antd/es/form";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import {
  EntityTypesSupportingFieldCustomization,
  Group,
} from "@mapmycustomers/shared/types/entity";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import { ChildLayout, LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import LayoutSchemaFieldDefaultValue from "@mapmycustomers/shared/types/layout/LayoutSchemaFieldDefaultValue";

import {
  updateChildLayout,
  updateLayout,
} from "@app/scene/settings/component/FormLayouts/store/actions";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";

const useDefaultValueChange = (
  form: FormInstance,
  activeSchema: LayoutSchemaField[],
  entityType: EntityTypesSupportingFieldCustomization,
  schemaField: LayoutSchemaField | undefined,
  onUpdateChildLayout: typeof updateChildLayout,
  onUpdateLayout: typeof updateLayout,
  activeChildLayout?: ChildLayout["id"]
) => {
  return useCallback(
    (fieldValue: LayoutSchemaFieldDefaultValue) => {
      let updatedSchema;
      if (schemaField) {
        if (schemaField.field === "funnelId") {
          updatedSchema = activeSchema.map((schemaRow: LayoutSchemaField) => {
            if (fieldValue?.length) {
              if ((fieldValue[0] as FunnelStageValue)["stage"]) {
                if (schemaRow.field === "dealStageId") {
                  return {
                    ...schemaRow,
                    defaultValue: [(fieldValue[0] as FunnelStageValue)["stage"].id],
                  };
                } else if (schemaRow.field === "funnelId") {
                  return {
                    ...schemaRow,
                    defaultValue: undefined,
                  };
                }
                return schemaRow;
              } else if ((fieldValue[0] as FunnelStageValue)["funnel"]) {
                if (schemaRow.field === "dealStageId") {
                  return {
                    ...schemaRow,
                    defaultValue: undefined,
                  };
                } else if (schemaRow.field === "funnelId") {
                  return {
                    ...schemaRow,
                    defaultValue: [(fieldValue[0] as FunnelStageValue)["funnel"].id],
                  };
                }
                return schemaRow;
              }
              return schemaRow;
            } else {
              return schemaRow.field === "dealStageId" || schemaRow.field === "funnelId"
                ? { ...schemaRow, defaultValue: undefined }
                : schemaRow;
            }
          });
        } else {
          updatedSchema = activeSchema.map((schemaRow: LayoutSchemaField) =>
            schemaRow.field === schemaField.field
              ? { ...schemaRow, defaultValue: fieldValue }
              : schemaRow
          );
        }

        if (activeChildLayout) {
          onUpdateChildLayout({ schema: updatedSchema });
        } else {
          onUpdateLayout({ schema: updatedSchema });
        }

        const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);

        if (!field) {
          return;
        }

        if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD)) {
          form.setFieldValue(
            ActivityFieldName.ACTIVITY_TYPE,
            fieldValue ? fieldValue[0] : undefined
          );
        } else if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
          form.setFieldValue(
            DealFieldName.FUNNEL,
            fieldValue && (fieldValue[0] as FunnelStageValue).funnel
              ? (fieldValue[0] as FunnelStageValue).funnel.id
              : undefined
          );
          form.setFieldValue(
            DealFieldName.STAGE,
            fieldValue && (fieldValue[0] as FunnelStageValue).stage
              ? (fieldValue[0] as FunnelStageValue).stage.id
              : undefined
          );
        } else if (schemaField.fieldType === SchemaFieldType.GROUP) {
          form.setFieldValue(
            field.name,
            fieldValue ? new Set(fieldValue as Group["id"][]) : undefined
          );
        } else if (isCustomField(field)) {
          const definition = field.customFieldData;
          form.setFieldValue(["customFields", String(definition?.id)], {
            customField: { id: definition?.id },
            value: fieldValue,
          });
        } else {
          form.setFieldValue(
            ["default", schemaField.field],
            fieldValue ? fieldValue[0] : undefined
          );
        }
      }
    },
    [
      activeChildLayout,
      activeSchema,
      entityType,
      form,
      onUpdateChildLayout,
      onUpdateLayout,
      schemaField,
    ]
  );
};

export default useDefaultValueChange;
